import React, { forwardRef, useImperativeHandle } from "react";
import { Breakpoint, DialogContent } from "@mui/material";

import CreateEditTemplate from "@/views/Doctor/Prescription/CreateEditTemplate";
import AvailabilitySettingDesktop from "@/views/Doctor/AvailabilitySettings/AvailabilitySettingDesktop";
import BeforeAdmitPatientPopUps from "@/views/Doctor/IPDashboard/PreAdmitPatientPopUps";
import AdmitPatient from "@/views/Doctor/IPDashboard/AdmitPatient";
import UniversalSearch from "@/views/Common/UniversalSearch";
import Confirmation from "@/components/Confirmation";
import HelpSupport from "@/views/Doctor/HelpSupport";
import MakeAppointment from "@/components/MakeAppointment";
import VerifyOTP from "@/components/Verification/VerifyOTP";
import EnterPatientDetail from "@/components/MakeAppointment/EnterPatientDetail";
import GenericSuccessAlert from "@/components/GenericSuccessAlert";
import Dialog from "@/components/Dialog";
import DoctorProfileDetails from "@/views/Common/DoctorProfile";
import ProfileDetail from "@/views/Common/Profile";
import Profile from "@/views/Doctor/Profile";
import Notification from "@/views/Common/Notification";
import CreateEditAvailability from "@/views/Doctor/AvailabilitySettings/CreateEditAvailability";
import ClinicalTeams from "@/components/ClinicalTeams";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { resetPatientDetailErrorMessages } from "@/store/slices/doctor/checkMobilePatientExistOrNot";

import useLogout from "@/utils/hooks/useLogout";
import useMakeAppointment from "@/utils/hooks/useMakeAppointment";
import { storage } from "@/utils/Storage";

import { IRoleType } from "@/models/role";
import {
  IAddPatient,
  IDialogMappings,
  IDoctorAvailabilityPlatform,
  IKeyPairsValues,
  IMakeAppointmentEvent,
  IProfileDropdownEnums,
} from "@/models/doctor";
import { ISessionKeys } from "@/models";

import { ITopBarItems } from "@/store/slices/doctor/models/topNavigationSliceModel";
import { config, setIsShowNotification } from "@/store/slices/config";
import { setSelectedAvailabilitySettingDetail } from "@/store/slices/doctor/availabilitySettings";
import { resetIPdashboard } from "@/store/slices/doctor/ipDashboard";

interface IProps {
  openCloseDoctorProfileDropdown: (identifier: IProfileDropdownEnums) => void;
  openCloseMobileCheckOTPVerify: (field: IAddPatient, value: boolean) => void;
}
const Overlays = forwardRef((props: IProps, ref) => {
  const { openCloseDoctorProfileDropdown, openCloseMobileCheckOTPVerify } =
    props;

  const topBarItems: ITopBarItems = useAppSelector(
    (state) => state.topNavigation.topBarItems
  );
  const { mobileCheck, admiPatient } = useAppSelector(
    (state) => state.topNavigation.addPatient
  );
  const { isShowUniversalSearch, isShowNotification } =
    useAppSelector(config) || {};

  const appointmentDetail = useAppSelector((state) => state.appointment);
  const dispatch = useAppDispatch();

  const { logout } = useLogout();
  const {
    makeAppointment,
    slotNo,
    openScheduleModalHandler,
    navigateToMobileVerify,
    handleCreatePatientVerifyOTP,
    createAppointmentHandler,
    sendOtpToCreatePatient,
    otpId,
    clearEnterPatientStates,
    setSelectedTab,
    selectedTab,
    backMakeAppointmentHandler,
  } = useMakeAppointment();

  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);

  const doctorOrJrDoctorRole =
    roleType === IRoleType.DOCTOR || roleType === IRoleType.JR_DOCTOR;

  const dialogKeys: IKeyPairsValues = {
    isProfileOpen: IProfileDropdownEnums.PROFILE,
    isReviewOpen: IProfileDropdownEnums.REVIEW,
    isTemplatesOpen: IProfileDropdownEnums.TEMPLATES,
    isAvailabilityOpen: IProfileDropdownEnums.AVAILABILITY_SETTINGS,
    isCreateEditAvailabilityOpen:
      IProfileDropdownEnums.CREATE_EDIT_AVAILABILITY,
    isClinicalTeamOpen: IProfileDropdownEnums.CLINICAL_TEAM,
  };
  const { isCreateTemplate } = useAppSelector(
    (state) => state.prescription.template
  );
  const activeDialogKey = Object.keys(dialogKeys).find(
    (key) => topBarItems?.[key as keyof ITopBarItems]
  );

  const dialogMappings: IDialogMappings = {
    isProfileOpen: {
      component: doctorOrJrDoctorRole ? (
        <DoctorProfileDetails />
      ) : (
        <ProfileDetail />
      ),
      size: doctorOrJrDoctorRole ? "sm" : "md",
    },
    isReviewOpen: { component: <Profile />, size: "sm" },
    isTemplatesOpen: { component: <CreateEditTemplate />, size: "lg" },
    isAvailabilityOpen: {
      component: <AvailabilitySettingDesktop />,
      size: "lg",
    },
    isCreateEditAvailabilityOpen: {
      component: <CreateEditAvailability />,
      size: "sm",
    },
    isClinicalTeamOpen: {
      component: <ClinicalTeams />,
      size: "md",
    },
  };

  const activeKey = Object.keys(dialogMappings).find(
    (key) => topBarItems?.[key as keyof ITopBarItems]
  );

  const dialogConfig = activeKey
    ? dialogMappings[activeKey as keyof IDialogMappings]
    : { component: <></>, size: "md" };

  useImperativeHandle(ref, () => ({
    createAppointmentHandler,
  }));

  return (
    <>
      <Dialog
        fullWidth
        maxWidth={dialogConfig?.size as Breakpoint}
        isOpen={!!activeDialogKey}
        closeHandler={
          activeKey === "isTemplatesOpen" && isCreateTemplate
            ? undefined
            : () => {
                if (activeKey === "isCreateEditAvailabilityOpen") return;
                if (activeKey === "isAvailabilityOpen") {
                  dispatch(setSelectedAvailabilitySettingDetail(null));
                }
                if (activeDialogKey) {
                  openCloseDoctorProfileDropdown(
                    dialogKeys[activeDialogKey] as IProfileDropdownEnums
                  );
                }
              }
        }
        paperProps={{
          sx: {
            ...((activeKey === "isAvailabilityOpen" ||
              activeKey === "isTemplatesOpen" ||
              activeKey === "isCreateEditAvailabilityOpen") && {
              borderRadius: "2.4rem !important",
            }),
            ...(activeKey === "isCreateEditAvailabilityOpen" && {
              maxWidth: "77rem",
            }),
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
          }}
        >
          {dialogConfig?.component}
        </DialogContent>
      </Dialog>

      {mobileCheck && <BeforeAdmitPatientPopUps />}
      {admiPatient && (
        <AdmitPatient
          isOpen={admiPatient}
          closeHandler={() => {
            openCloseMobileCheckOTPVerify(IAddPatient.ADMIT_PATIENT, false);
            dispatch(resetPatientDetailErrorMessages());
          }}
        />
      )}
      {isShowUniversalSearch && <UniversalSearch />}

      <Confirmation
        isOpen={topBarItems.isLogoutOpen}
        closeHandler={() =>
          openCloseDoctorProfileDropdown(IProfileDropdownEnums.LOGOUT)
        }
        title="logout"
        alterMessage={"logout_alert_message"}
        actionHandler={logout}
      />
      {topBarItems?.isHelpSupportOpen && (
        <HelpSupport
          isOpen={topBarItems?.isHelpSupportOpen}
          closeHandler={() =>
            openCloseDoctorProfileDropdown(IProfileDropdownEnums.HELP_SUPPORT)
          }
        />
      )}
      {topBarItems?.isMakeAppointmentFromNavBar && makeAppointment.creation && (
        <MakeAppointment
          selectedTab={
            doctorOrJrDoctorRole ? selectedTab : IMakeAppointmentEvent.IN_CLINIC
          }
          platform={
            doctorOrJrDoctorRole
              ? IDoctorAvailabilityPlatform.BOTH
              : IDoctorAvailabilityPlatform.IN_CLINIC
          }
          setSelectedTab={setSelectedTab}
          closeScheduleModal={clearEnterPatientStates}
          openScheduleModalHandler={openScheduleModalHandler}
          isOpen={makeAppointment.creation}
        />
      )}
      {topBarItems?.isMakeAppointmentFromNavBar && makeAppointment.otp && (
        <VerifyOTP
          handleOTPSubmit={handleCreatePatientVerifyOTP}
          mobileNo={appointmentDetail?.makePatientAppointment?.mobileNo}
          navigateToMobileVerify={navigateToMobileVerify}
          resendOTP={() =>
            sendOtpToCreatePatient(
              appointmentDetail?.makePatientAppointment?.countryCode
            )
          }
        />
      )}
      {topBarItems?.isMakeAppointmentFromNavBar &&
        (makeAppointment.mobileCheck || makeAppointment.patientDetail) && (
          <EnterPatientDetail
            notExistPatient={otpId}
            backHandler={backMakeAppointmentHandler}
            closeModalHandler={clearEnterPatientStates}
            openScheduleModalHandler={openScheduleModalHandler}
            isScheduleCreateOpen={makeAppointment}
            isOpen={
              makeAppointment.mobileCheck || makeAppointment.patientDetail!
            }
          />
        )}
      {topBarItems?.isMakeAppointmentFromNavBar && makeAppointment.success && (
        <GenericSuccessAlert
          type={appointmentDetail?.makePatientAppointment?.type}
          slotNumber={+slotNo}
          name={appointmentDetail?.makePatientAppointment?.name}
          age={appointmentDetail?.makePatientAppointment?.age}
          date={appointmentDetail?.makePatientAppointment?.dateSlotTime}
          isOpen={makeAppointment.success}
          closeScheduleModal={clearEnterPatientStates}
        />
      )}
      {isShowNotification && (
        <Notification
          isOpen={isShowNotification}
          closeHandler={() => dispatch(setIsShowNotification(false))}
        />
      )}
    </>
  );
});

export default Overlays;
